import React, { useState, useEffect } from 'react';
import { Button, Navbar, Nav, Container, ListGroup, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useDebug } from '../contexts/DebugContext';
import { isAdmin as checkIsAdmin, getSmartRedirectPath } from '../shared/accountCache';
import { safeFormatDate } from '../shared/utils';
import { isRTL } from '../i18n/config';
import VersionRefreshIcon from './VersionRefreshIcon';
import LanguageSelector from './LanguageSelector';

/**
 * Reusable sliding menu component that can be used for both regular navigation
 * and for plan details with day navigation
 * 
 * @param {Object} props Component props
 * @param {Array} props.days Optional array of days for plan navigation
 * @param {Function} props.onDaySelect Callback when a day is selected
 * @param {String} props.title Optional title for the header
 */
export default function SlidingMenu({ days, onDaySelect, title = "trip147" }) {
  const { t, i18n } = useTranslation();
  const { currentUser, logout } = useAuth();
  const { debugMode } = useDebug();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [daysExpanded, setDaysExpanded] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check if user is admin (using cached data)
  useEffect(() => {
    if (!currentUser) {
      setIsAdmin(false);
      return;
    }
    
    const checkAdmin = async () => {
      try {
        const adminStatus = await checkIsAdmin();
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdmin();
  }, [currentUser]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navToPage = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  // Handle day selection
  const handleDaySelect = (dayId) => {
    if (onDaySelect) {
      onDaySelect(dayId);
      setMenuOpen(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('.mobile-sliding-menu') && 
          !event.target.closest('.mobile-menu-toggle') &&
          !event.target.closest('.dropdown-menu') &&
          !event.target.closest('.dropdown-toggle')) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scrolling when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      <Navbar expand="lg" bg="dark" variant="dark" className="py-2 mb-4" fixed="top">
        <Container className="d-flex align-items-center justify-content-between">
          <Navbar.Brand className="text-white mb-0 d-flex align-items-center" style={{ flexShrink: 1, minWidth: 0, paddingLeft: isRTL(i18n.language) ? 0 : undefined, paddingRight: isRTL(i18n.language) ? 0 : undefined }}>
            <img src="/logo.svg" alt="trip147" style={{ width: '30px', height: '30px', marginRight: isRTL(i18n.language) ? 0 : '8px', marginLeft: isRTL(i18n.language) ? '8px' : 0, flexShrink: 0 }} />
            <span className="text-truncate">{title}</span>
          </Navbar.Brand>
          <div className="flex-grow-1 d-none d-md-flex justify-content-center">
            <VersionRefreshIcon />
          </div>
          <div 
            className="d-flex align-items-center gap-2" 
            style={{ 
              flexShrink: 0
            }}
          >
            <div className="d-none d-md-block text-white fw-normal" style={{fontSize: '1rem'}}>
              <i className={`bi bi-person-circle ${isRTL(i18n.language) ? 'ms-2' : 'me-2'}`}></i>{t('common.welcome')}, {currentUser?.name || t('common.user')}
            </div>
            <LanguageSelector variant="outline-light" size="sm" />
            <Button 
              variant="outline-light"
              className="mobile-menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-controls="mobile-menu"
              aria-expanded={menuOpen}
            >
              <span className="navbar-toggler-icon"></span>
            </Button>
          </div>
        </Container>
      </Navbar>

      {/* Overlay when menu is open */}
      <div className={`menu-overlay ${menuOpen ? 'active' : ''}`} 
          onClick={() => setMenuOpen(false)}></div>

      {/* Simple sliding menu with full height usage */}
      <div className={`mobile-sliding-menu ${menuOpen ? 'open' : ''}`} id="mobile-menu">
        <div className="menu-content">
          {/* App name at the top */}
          <div 
            className="menu-header text-white fw-bold d-flex align-items-center justify-content-between" 
            style={{fontSize: '1.3rem', letterSpacing: '1px'}}
          >
            {/* Logo - left in LTR, right in RTL */}
            <img 
              src="/logo.svg" 
              alt="trip147" 
              style={{ 
                width: '35px', 
                height: '35px',
                order: isRTL(i18n.language) ? 2 : 0
              }} 
            />
            {/* trip147 with homepage icon - right in LTR, left in RTL */}
            <div
              className="d-flex align-items-center"
              style={{
                cursor: 'pointer',
                order: isRTL(i18n.language) ? 0 : 2,
                gap: '8px',
                flexDirection: isRTL(i18n.language) ? 'row-reverse' : 'row'
              }}
              onClick={() => {
                navToPage('/');
              }}
            >
              <span>trip147</span>
              <i className="bi bi-house" style={{ fontSize: '1.1rem' }}></i>
            </div>
          </div>
          
          {/* Main navigation items */}
          <Nav className="mobile-nav">
            <Nav.Link 
              className="text-white menu-item" 
              onClick={async () => {
                const redirectPath = await getSmartRedirectPath(currentUser, false);
                navToPage(redirectPath);
              }}
            >
              <i className="bi bi-list-check me-3"></i>{t('navigation.trips')}
            </Nav.Link>
            <Nav.Link 
              className="text-white menu-item" 
              onClick={() => navToPage('/trip-creator')}
            >
              <i className="bi bi-magic me-3"></i>{t('navigation.newTrip')}
            </Nav.Link>
            <Nav.Link 
              className="text-white menu-item" 
              onClick={() => navToPage('/copy-itinerary')}
            >
              <i className="bi bi-copy me-3"></i>{t('navigation.copyItinerary', { defaultValue: 'Copy Itinerary' })}
            </Nav.Link>
            <Nav.Link 
              className="text-white menu-item" 
              onClick={() => navToPage('/pricing')}
            >
              <i className="bi bi-tag me-3"></i>{t('navigation.pricing')}
            </Nav.Link>
            <Nav.Link 
              className="text-white menu-item" 
              onClick={() => navToPage('/luggage-tags')}
            >
              <i className="bi bi-qr-code me-3"></i>{t('navigation.luggageTags')}
            </Nav.Link>
            <Nav.Link 
              className="text-white menu-item" 
              onClick={() => navToPage('/groups')}
            >
              <i className="bi bi-people me-3"></i>{t('navigation.groups')}
            </Nav.Link>
            {isAdmin ? (
              <>
                <Nav.Link 
                  className="text-white menu-item" 
                  onClick={() => navToPage('/inbox')}
                >
                  <i className="bi bi-inbox me-3"></i>{t('navigation.inboxPlan')}
                </Nav.Link>
                <Nav.Link 
                  className="text-white menu-item" 
                  onClick={() => navToPage('/inbox-contact-us')}
                >
                  <i className="bi bi-envelope me-3"></i>{t('navigation.inboxContactUs')}
                </Nav.Link>
              </>
            ) : (
              <Nav.Link 
                className="text-white menu-item" 
                onClick={() => navToPage('/inbox')}
              >
                <i className="bi bi-inbox me-3"></i>{t('navigation.inbox')}
              </Nav.Link>
            )}
            <Nav.Link 
              className="text-white menu-item" 
              onClick={() => navToPage('/settings')}
            >
              <i className="bi bi-gear me-3"></i>{t('navigation.settings')}
            </Nav.Link>
            
            {/* Debug Settings - always show to admin so they can toggle debug mode */}
            {isAdmin && (
              <Nav.Link 
                className="text-white menu-item" 
                onClick={() => navToPage('/debug-settings')}
              >
                <i className="bi bi-gear me-3"></i>{t('navigation.debugSettings')}
              </Nav.Link>
            )}
            {/* Admin Dashboard - only show to admin users */}
            {isAdmin && (
              <Nav.Link 
                className="text-white menu-item" 
                onClick={() => navToPage('/admin')}
              >
                <i className="bi bi-shield-check me-3"></i>{t('navigation.adminDashboard')}
              </Nav.Link>
            )}
          </Nav>

          {/* Days navigation section - only shown if days are provided */}
          {days && days.length > 0 && (
            <div className="days-section">
              <div 
                className="days-header d-flex justify-content-between align-items-center"
                onClick={() => setDaysExpanded(!daysExpanded)}
              >
                <span><i className="bi bi-calendar me-2"></i> {t('navigation.days')}</span>
                <i className={`bi ${daysExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
              </div>
              
              {daysExpanded && (
                <ListGroup variant="flush" className="days-list">
                  {days.map((day, index) => (
                    <ListGroup.Item 
                      key={day.id}
                      action
                      className="bg-transparent border-0 text-white py-1"
                      onClick={() => handleDaySelect(day.id)}
                    >
                      <div className="d-flex align-items-center">
                        <i className="bi bi-calendar-date me-2"></i>
                        <span>Day {index + 1} - {safeFormatDate(day.date, 'MMM d')}</span>
                        <Badge bg="light" text="dark" pill className="ms-2">
                          {day.activities?.length || 0}
                        </Badge>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          )}
          
          {/* Logout as a normal menu item */}
          <Nav className="mobile-nav mt-2">
            <Nav.Link 
              className="text-white menu-item" 
              onClick={handleLogout}
            >
              <i className="bi bi-box-arrow-right me-3"></i>{t('navigation.logout')}
            </Nav.Link>
          </Nav>
        </div>
      </div>
    </>
  );
} 